import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/hooks/useSession';
import { MultiSelectPopover } from '@/shared/components/MultiSelectPopover';
import {
  Eye,
  EyeOff,
  RefreshCw,
  Check,
  X,
  Loader2,
  ChevronsUpDown,
  Plus,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TagsSelector } from '../TagsSelector';
import {
  useCreateUser,
  useNewUserOptions,
  useAccountSearch,
  useOrganisation,
} from '../../hooks';
import { checkEmailUnique, checkUsernameUnique } from '../../api';
import { validatePassword, generatePassword } from '../../utils/passwordValidation';
import { getOrgFeatureFlags } from '../../utils/constants';
import type {
  AccountOption,
  AdminLevel,
  ContactMethod,
  ContactMethodType,
  CreateUserInput,
  Organisation,
} from '../../types';

const MAX_PASSWORD_LENGTH = 128;
const MIN_USERNAME_LENGTH = 8;
const MAX_LOGIN_TTL_HOURS = 12;
const DEFAULT_LOGIN_TTL_HOURS = '3';
const EMPTY_CONTACT_METHOD: ContactMethod = {
  type: '',
  contact: '',
  ext: '',
  preferred: false,
};

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId?: string;
  defaultOrganisationId?: string;
  lockOrganisation?: boolean;
}

const ADMIN_LEVEL_OPTIONS: Array<{ value: AdminLevel; label: string }> = [
  { value: 'standard user', label: 'Standard User' },
  { value: 'group admin', label: 'Group Admin' },
  { value: 'full admin', label: 'Full Admin' },
  { value: 'auditor', label: 'Auditor' },
];

const CONTACT_METHOD_OPTIONS: Array<{ value: ContactMethodType; label: string }> = [
  { value: 'phone', label: 'Phone' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'alphatelepage', label: 'Alpha Pager' },
  { value: 'numtelepage', label: 'Numeric Pager' },
];

function formatOrganisationLabel(organisation: Organisation) {
  const accountNumber = organisation.accountNumber
    ?? (organisation.org_num != null ? String(organisation.org_num).padStart(4, '0') : null);

  if (!accountNumber) return organisation.name;

  const normalizedNumber = accountNumber.replace(/^A-/, '').split('-')[0];
  return normalizedNumber ? `#${normalizedNumber} - ${organisation.name}` : organisation.name;
}

function formatAccountLabel(account: Pick<AccountOption, 'name' | 'accountNumber'>) {
  if (!account.accountNumber) return account.name;
  return `#${account.accountNumber} - ${account.name}`;
}

function normalizeUsername(value: string) {
  return value.toLowerCase().replace(/\s+/g, '');
}

function sanitizeOrganisationSlug(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w]/g, '')
    .toLowerCase();
}

function buildSubaccountEmail(username: string, organisationName?: string) {
  const normalizedUsername = normalizeUsername(username);
  const normalizedOrganisation = organisationName ? sanitizeOrganisationSlug(organisationName) : '';

  if (!normalizedUsername || !normalizedOrganisation) {
    return '';
  }

  return `${normalizedOrganisation}+${normalizedUsername}@atek.io`;
}

function isContactMethodComplete(method: ContactMethod) {
  return Boolean(method.type && method.contact?.trim());
}

function isContactMethodStarted(method: ContactMethod) {
  return Boolean(method.type || method.contact?.trim() || method.ext?.trim());
}

function normalizeContactMethods(contactMethods: ContactMethod[]) {
  return contactMethods
    .filter(isContactMethodComplete)
    .map((method) => ({
      type: method.type,
      contact: method.contact?.trim(),
      ext: method.ext?.trim() || undefined,
      preferred: method.preferred === true,
      tags: method.tags,
    }));
}

export function CreateUserModal({
  open,
  onOpenChange,
  accountId,
  defaultOrganisationId,
  lockOrganisation = false,
}: CreateUserModalProps) {
  const { user: sessionUser } = useSession();
  const featureFlags = getOrgFeatureFlags();
  const isFullAdmin = !!(sessionUser?.postgres_roles?.includes('full_admin') || sessionUser?.postgres_roles?.includes('director'));
  const adminLevelOptions = featureFlags.auditorRole
    ? ADMIN_LEVEL_OPTIONS
    : ADMIN_LEVEL_OPTIONS.filter((option) => option.value !== 'auditor');

  // Form state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [phone, setPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [mobile, setMobile] = useState('');
  const [company, setCompany] = useState('');
  const [position, setPosition] = useState('');
  const [language, setLanguage] = useState<'en' | 'fr'>('en');
  const [organisationId, setOrganisationId] = useState('');
  const [adminLevel, setAdminLevel] = useState<AdminLevel>('standard user');
  const [enabled, setEnabled] = useState(true);
  const [isSubaccount, setIsSubaccount] = useState(false);
  const [forcePasswordReset, setForcePasswordReset] = useState(false);
  const [loginTtlHrs, setLoginTtlHrs] = useState(DEFAULT_LOGIN_TTL_HOURS);
  const [selectedUsergroups, setSelectedUsergroups] = useState<string[]>([]);
  const [selectedSensorgroups, setSelectedSensorgroups] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [contactMethods, setContactMethods] = useState<ContactMethod[]>([]);

  // Uniqueness state
  const [usernameUnique, setUsernameUnique] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [emailUnique, setEmailUnique] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Mutations
  const createUserMutation = useCreateUser();
  const newUserOptionsQuery = useNewUserOptions(
    organisationId || undefined,
    lockOrganisation ? accountId : undefined,
  );

  // Debounced uniqueness checks
  const debouncedUsername = useDebounce(username, 500);
  const debouncedEmail = useDebounce(email, 500);

  // Track the latest uniqueness check requests to avoid race conditions
  const latestUsernameCheckRef = useRef<string | null>(null);
  const latestEmailCheckRef = useRef<string | null>(null);

  // Check username uniqueness when debounced username changes
  useEffect(() => {
    let isCurrent = true;

    if (!open) {
      latestUsernameCheckRef.current = null;
      return () => {
        isCurrent = false;
      };
    }

    const checkUsername = async () => {
      const trimmedUsername = debouncedUsername.trim();

      if (!trimmedUsername || trimmedUsername.length < MIN_USERNAME_LENGTH) {
        latestUsernameCheckRef.current = null;
        setUsernameUnique(null);
        setCheckingUsername(false);
        return;
      }

      const normalizedUsername = normalizeUsername(trimmedUsername);
      latestUsernameCheckRef.current = normalizedUsername;
      setCheckingUsername(true);

      try {
        const isUnique = await checkUsernameUnique(normalizedUsername);
        if (isCurrent && latestUsernameCheckRef.current === normalizedUsername) {
          setUsernameUnique(isUnique);
        }
      } catch {
        if (isCurrent && latestUsernameCheckRef.current === normalizedUsername) {
          setUsernameUnique(null);
        }
      } finally {
        if (isCurrent && latestUsernameCheckRef.current === normalizedUsername) {
          setCheckingUsername(false);
        }
      }
    };

    void checkUsername();

    return () => {
      isCurrent = false;
    };
  }, [debouncedUsername, open]);

  // Check email uniqueness when debounced email changes
  useEffect(() => {
    let isCurrent = true;

    if (!open) {
      latestEmailCheckRef.current = null;
      return () => {
        isCurrent = false;
      };
    }

    const checkEmail = async () => {
      const normalizedEmail = debouncedEmail.trim().toLowerCase();

      if (!normalizedEmail || !normalizedEmail.includes('@')) {
        latestEmailCheckRef.current = null;
        setEmailUnique(null);
        setCheckingEmail(false);
        return;
      }

      latestEmailCheckRef.current = normalizedEmail;
      setCheckingEmail(true);

      try {
        const isUnique = await checkEmailUnique(normalizedEmail);
        if (isCurrent && latestEmailCheckRef.current === normalizedEmail) {
          setEmailUnique(isUnique);
        }
      } catch {
        if (isCurrent && latestEmailCheckRef.current === normalizedEmail) {
          setEmailUnique(null);
        }
      } finally {
        if (isCurrent && latestEmailCheckRef.current === normalizedEmail) {
          setCheckingEmail(false);
        }
      }
    };

    void checkEmail();

    return () => {
      isCurrent = false;
    };
  }, [debouncedEmail, open]);

  // Password validation
  const passwordValidation = validatePassword(password);
  const { data: selectedOrganisation } = useOrganisation(organisationId);
  const generatedSubaccountEmail = buildSubaccountEmail(username, selectedOrganisation?.name);

  useEffect(() => {
    if (!open || !isSubaccount) return;
    setEmail((currentEmail) => (currentEmail === generatedSubaccountEmail ? currentEmail : generatedSubaccountEmail));
  }, [generatedSubaccountEmail, isSubaccount, open]);

  // Handle generate password
  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    setShowPassword(true); // Show so user can see the generated password
  };

  // Reset form
  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setFirstname('');
    setLastname('');
    setPhone('');
    setExtension('');
    setMobile('');
    setCompany('');
    setPosition('');
    setLanguage('en');
    setOrganisationId(defaultOrganisationId || '');
    setAdminLevel('standard user');
    setEnabled(true);
    setIsSubaccount(false);
    setForcePasswordReset(false);
    setLoginTtlHrs(DEFAULT_LOGIN_TTL_HOURS);
    setSelectedUsergroups([]);
    setSelectedSensorgroups([]);
    setSelectedTags([]);
    setDescription('');
    setContactMethods([]);
    setUsernameUnique(null);
    setCheckingUsername(false);
    setEmailUnique(null);
    setCheckingEmail(false);
  };

  useEffect(() => {
    if (!open) return;

    setOrganisationId(defaultOrganisationId || '');
    setSelectedUsergroups([]);
    setSelectedSensorgroups([]);
    setSelectedTags([]);
    setDescription('');
  }, [open, defaultOrganisationId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSubaccountChange = (checked: boolean) => {
    setIsSubaccount(checked);
    setEmailUnique(null);

    if (checked) {
      setEmail(generatedSubaccountEmail);
      return;
    }

    if (email === generatedSubaccountEmail) {
      setEmail('');
    }
  };

  const updateContactMethod = (index: number, patch: Partial<ContactMethod>) => {
    setContactMethods((currentMethods) =>
      currentMethods.map((method, currentIndex) =>
        currentIndex === index ? { ...method, ...patch } : method,
      ),
    );
  };

  const removeContactMethod = (index: number) => {
    setContactMethods((currentMethods) => currentMethods.filter((_, currentIndex) => currentIndex !== index));
  };

  const addContactMethod = () => {
    setContactMethods((currentMethods) => [...currentMethods, { ...EMPTY_CONTACT_METHOD }]);
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedContactMethods = normalizeContactMethods(contactMethods);
    const parsedLoginTtl = loginTtlHrs.trim() ? Number.parseInt(loginTtlHrs, 10) : undefined;

    const data: CreateUserInput = {
      username: username.trim(),
      email: email.trim().toLowerCase(),
      password,
      firstname: firstname.trim(),
      lastname: lastname.trim(),
      organisation: organisationId,
      admin: adminLevel,
      usergroups: selectedUsergroups,
      sensorgroups: selectedSensorgroups,
      phone: phone.trim() || undefined,
      extension: extension.trim() || undefined,
      mobile: mobile.trim() || undefined,
      company: company.trim() || undefined,
      position: position.trim() || undefined,
      language,
      enabled,
      is_subaccount: isSubaccount,
      force_password_reset: forcePasswordReset,
      contact_methods: normalizedContactMethods.length ? normalizedContactMethods : undefined,
      login_ttl_hrs: parsedLoginTtl,
      tags: selectedTags.length ? selectedTags : undefined,
      description: description.trim() || undefined,
    };

    try {
      await createUserMutation.mutateAsync(data);
      resetForm();
      onOpenChange(false);
    } catch {
      // Error is handled by the mutation hook
    }
  };

  // Form validation
  const hasValidUsernameLength = username.trim().length >= MIN_USERNAME_LENGTH;
  const hasValidLoginTtl =
    loginTtlHrs.trim() === ''
    || (/^\d+$/.test(loginTtlHrs.trim())
      && Number.parseInt(loginTtlHrs, 10) >= 0
      && Number.parseInt(loginTtlHrs, 10) <= MAX_LOGIN_TTL_HOURS);
  const hasInvalidContactMethods = contactMethods.some((method) => {
    if (!isContactMethodStarted(method)) return false;
    if (!isContactMethodComplete(method)) return true;
    if (method.type === 'email') {
      return !method.contact?.includes('@');
    }
    return false;
  });
  const isFormValid =
    username.trim() &&
    hasValidUsernameLength &&
    usernameUnique === true &&
    email.trim() &&
    email.includes('@') &&
    emailUnique === true &&
    passwordValidation.isValid &&
    firstname.trim() &&
    lastname.trim() &&
    organisationId &&
    hasValidLoginTtl &&
    !hasInvalidContactMethods &&
    !newUserOptionsQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
          <DialogDescription>
            Create a new user account. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Username */}
            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <div className="relative">
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameUnique(null);
                  }}
                  placeholder="Enter username"
                  autoComplete="off"
                  className={cn(
                    usernameUnique === false && 'border-red-500 focus-visible:ring-red-500',
                  )}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingUsername && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {!checkingUsername && usernameUnique === true && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                  {!checkingUsername && usernameUnique === false && (
                    <X className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
              {username.trim().length > 0 && username.trim().length < MIN_USERNAME_LENGTH && (
                <p className="text-sm text-red-500">
                  Username must be at least {MIN_USERNAME_LENGTH} characters
                </p>
              )}
              {usernameUnique === false && (
                <p className="text-sm text-red-500">This username is already in use</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email {!isSubaccount ? '*' : ''}</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  disabled={isSubaccount}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailUnique(null);
                  }}
                  placeholder={isSubaccount ? 'Generated from username' : 'user@example.com'}
                  className={cn(
                    emailUnique === false && 'border-red-500 focus-visible:ring-red-500'
                  )}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingEmail && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {!checkingEmail && emailUnique === true && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                  {!checkingEmail && emailUnique === false && (
                    <X className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
              {emailUnique === false && (
                <p className="text-sm text-red-500">This email is already in use</p>
              )}
              {isSubaccount && (
                <p className="text-xs text-muted-foreground">
                  Generated as `{generatedSubaccountEmail || 'select an account and enter a username'}`
                </p>
              )}
            </div>

            {/* Account flags */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="user-without-email">User Without Email</Label>
                <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <Checkbox
                    id="user-without-email"
                    checked={isSubaccount}
                    onCheckedChange={(checked) => handleSubaccountChange(checked === true)}
                  />
                  <Label htmlFor="user-without-email" className="cursor-pointer font-normal">
                    Create as a subaccount without a personal email address
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Enabled</Label>
                <div className="flex items-center gap-3 rounded-md border px-3 py-2">
                  <Switch checked={enabled} onCheckedChange={setEnabled} />
                  <span className="text-sm">{enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    maxLength={MAX_PASSWORD_LENGTH}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePassword}
                  className="shrink-0"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Generate
                </Button>
              </div>
              {/* Password requirements */}
              {password && (
                <div className="grid grid-cols-2 gap-1 text-xs mt-2">
                  <PasswordRequirement met={passwordValidation.hasMinLength}>
                    8+ characters
                  </PasswordRequirement>
                  <PasswordRequirement met={passwordValidation.hasUppercase}>
                    Uppercase letter
                  </PasswordRequirement>
                  <PasswordRequirement met={passwordValidation.hasLowercase}>
                    Lowercase letter
                  </PasswordRequirement>
                  <PasswordRequirement met={passwordValidation.hasDigit}>
                    Number
                  </PasswordRequirement>
                  <PasswordRequirement met={passwordValidation.hasSpecialChar}>
                    Special character
                  </PasswordRequirement>
                </div>
              )}
            </div>

            {/* Security controls */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="force-password-reset">Force Password Reset</Label>
                <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                  <Checkbox
                    id="force-password-reset"
                    checked={forcePasswordReset}
                    onCheckedChange={(checked) => setForcePasswordReset(checked === true)}
                  />
                  <Label htmlFor="force-password-reset" className="cursor-pointer font-normal">
                    Require a password reset on first login
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-ttl">Session Timeout (hours)</Label>
                <Input
                  id="login-ttl"
                  type="number"
                  min={0}
                  max={MAX_LOGIN_TTL_HOURS}
                  step={1}
                  value={loginTtlHrs}
                  onChange={(e) => setLoginTtlHrs(e.target.value)}
                  placeholder={DEFAULT_LOGIN_TTL_HOURS}
                />
                {!hasValidLoginTtl && (
                  <p className="text-sm text-red-500">
                    Session timeout must be between 0 and {MAX_LOGIN_TTL_HOURS} hours
                  </p>
                )}
              </div>
            </div>

            {/* Name */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstname">First Name *</Label>
                <Input
                  id="firstname"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastname">Last Name *</Label>
                <Input
                  id="lastname"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-2">
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                  <Input
                    id="extension"
                    value={extension}
                    onChange={(e) => setExtension(e.target.value)}
                    placeholder="Ext"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="Mobile number"
                />
              </div>
            </div>

            {/* Notification methods */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Notification Methods</Label>
                <Button type="button" variant="outline" size="sm" onClick={addContactMethod}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add
                </Button>
              </div>
              {contactMethods.length === 0 ? (
                <p className="text-sm text-muted-foreground">No notification methods added</p>
              ) : (
                <div className="space-y-3">
                  {contactMethods.map((method, index) => {
                    const methodNeedsExtension =
                      method.type === 'phone' || method.type === 'numtelepage';

                    return (
                      <div
                        key={`${method.type || 'empty'}-${index}`}
                        className="grid gap-2 rounded-md border p-3 md:grid-cols-[180px_minmax(0,1fr)_120px_auto]"
                      >
                        <select
                          aria-label={`Notification method type ${index + 1}`}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={method.type || ''}
                          onChange={(e) => {
                            const nextType = (e.target.value || '') as ContactMethodType | '';
                            const nextTypeNeedsExtension =
                              nextType === 'phone' || nextType === 'numtelepage';

                            updateContactMethod(index, {
                              type: nextType,
                              ext: nextTypeNeedsExtension ? method.ext : '',
                            });
                          }}
                        >
                          <option value="">Select method</option>
                          {CONTACT_METHOD_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <Input
                          aria-label={`Notification method contact ${index + 1}`}
                          type={method.type === 'email' ? 'email' : 'text'}
                          value={method.contact || ''}
                          onChange={(e) => updateContactMethod(index, { contact: e.target.value })}
                          placeholder={method.type === 'email' ? 'you@example.com' : 'Contact value'}
                        />
                        {methodNeedsExtension ? (
                          <Input
                            aria-label={`Notification method extension ${index + 1}`}
                            value={method.ext || ''}
                            onChange={(e) => updateContactMethod(index, { ext: e.target.value })}
                            placeholder={method.type === 'numtelepage' ? 'Code' : 'Ext'}
                          />
                        ) : (
                          <div />
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeContactMethod(index)}
                          aria-label={`Remove notification method ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {hasInvalidContactMethods && (
                <p className="text-sm text-red-500">
                  Every notification method must include a type and contact value
                </p>
              )}
            </div>

            {/* Work Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="Job title"
                />
              </div>
            </div>

            {/* Language */}
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'fr')}>
                <SelectTrigger id="language" className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Organisation */}
            <div className="space-y-2">
              <Label htmlFor="organisation">Account *</Label>
              <AccountCombobox
                value={organisationId}
                disabled={lockOrganisation}
                onSelect={(value) => {
                  setOrganisationId(value);
                  setSelectedUsergroups([]);
                  setSelectedSensorgroups([]);
                  setSelectedTags([]);
                  setDescription('');
                }}
              />
            </div>

            {/* Admin level */}
            <div className="space-y-2">
              <Label htmlFor="admin-level">Admin Level</Label>
              <Select value={adminLevel} onValueChange={(value) => setAdminLevel(value as AdminLevel)}>
                <SelectTrigger id="admin-level" className="w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {adminLevelOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {organisationId && isFullAdmin && (
              <TagsSelector
                selectedTags={selectedTags}
                onChange={setSelectedTags}
                organisationId={organisationId}
              />
            )}

            {/* Usergroups */}
            {organisationId && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>User Groups</Label>
                  {newUserOptionsQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    <MultiSelectPopover
                      value={selectedUsergroups}
                      options={(newUserOptionsQuery.data?.usergroups || []).map((group) => ({
                        id: group._id,
                        label: group.name,
                      }))}
                      onChange={setSelectedUsergroups}
                      placeholder="Select user groups..."
                      searchPlaceholder="Search user groups..."
                      emptyMessage="No user groups available for this organisation"
                      noMatchesMessage="No matching groups"
                      showSelectAll
                      selectAllLabel="Select all user groups"
                    />
                  )}
                  {!newUserOptionsQuery.isLoading && selectedUsergroups.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {selectedUsergroups.length} selected
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Sensor Groups</Label>
                  {newUserOptionsQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    <MultiSelectPopover
                      value={selectedSensorgroups}
                      options={(newUserOptionsQuery.data?.sensorgroups || []).map((group) => ({
                        id: group._id,
                        label: group.name,
                      }))}
                      onChange={setSelectedSensorgroups}
                      placeholder="Select sensor groups..."
                      searchPlaceholder="Search sensor groups..."
                      emptyMessage="No sensor groups available for this organisation"
                      noMatchesMessage="No matching groups"
                      showSelectAll
                      selectAllLabel="Select all sensor groups"
                    />
                  )}
                  {!newUserOptionsQuery.isLoading && selectedSensorgroups.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {selectedSensorgroups.length} selected
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add notes or context for this account profile"
                    rows={4}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid || createUserMutation.isPending}>
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordRequirement({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <div className={cn('flex items-center gap-1', met ? 'text-green-600' : 'text-muted-foreground')}>
      {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{children}</span>
    </div>
  );
}

function AccountCombobox({
  value,
  disabled = false,
  onSelect,
}: {
  value: string;
  disabled?: boolean;
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const searchQuery = debouncedSearch.trim();
  const accountsQuery = useAccountSearch(searchQuery, open && !disabled);
  const searchResults = accountsQuery.data || [];
  const selectedAccount = searchResults.find(
    (account) => account.mongoOrganisationId === value,
  );
  const { data: selectedOrganisation } = useOrganisation(
    !selectedAccount && value ? value : undefined,
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const triggerLabel = selectedAccount
    ? formatAccountLabel(selectedAccount)
    : selectedOrganisation
      ? formatOrganisationLabel(selectedOrganisation)
      : null;

  return (
    <Popover open={disabled ? false : open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          id="organisation"
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {triggerLabel ? (
            <span className="truncate">{triggerLabel}</span>
          ) : (
            <span className="text-muted-foreground">Select account</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search accounts..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No accounts found.</CommandEmpty>
            <CommandGroup>
              {accountsQuery.isLoading ? (
                <CommandItem value="__loading__" disabled>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {searchQuery ? 'Searching...' : 'Loading accounts...'}
                </CommandItem>
              ) : null}
              {searchResults.map((account) => (
                <CommandItem
                  key={account.id}
                  value={account.id}
                  onSelect={() => {
                    onSelect(account.mongoOrganisationId);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === account.mongoOrganisationId ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{formatAccountLabel(account)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
